import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Award, CheckCircle2, AlertTriangle, Download, Shield, FileText, Lock, Stethoscope, Activity, Database, Users, Heart, Wrench, ClipboardCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const PILLAR_ICONS = {
  1: Heart, 2: Shield, 3: Lock, 4: Stethoscope, 5: FileText, 6: Lock,
  7: Users, 8: Database, 9: ClipboardCheck, 10: AlertTriangle, 11: Wrench, 12: Activity,
};

export default function ComplianceCertificate() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/compliance-export/certificate/data`, { withCredentials: true });
      setData(data);
    } catch { /* ignore */ }
    setLoading(false);
  };

  const downloadPDF = async () => {
    try {
      const response = await axios.get(`${API}/api/compliance-export/certificate`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a'); a.href = url;
      a.download = `Compliance_Certificate_${new Date().toISOString().slice(0, 7)}.pdf`;
      a.click(); window.URL.revokeObjectURL(url);
      toast.success('Certificate downloaded');
    } catch { toast.error('Download failed'); }
  };

  if (loading) return <DashboardLayout><div className="flex items-center justify-center h-64"><p className="text-muted-foreground">Loading compliance data...</p></div></DashboardLayout>;

  const passCount = data?.pass_count || 0;
  const totalPillars = data?.total || 12;
  const allPass = passCount === totalPillars;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="compliance-certificate-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-heading font-bold text-foreground flex items-center gap-2">
              <Award className="w-6 h-6 text-[#00A6A0]" />Compliance Certificate
            </h1>
            <p className="text-sm text-muted-foreground mt-1">Monthly compliance status for board reporting - {data?.month}</p>
          </div>
          <Button size="sm" className="btn-primary" onClick={downloadPDF} data-testid="download-certificate-btn">
            <Download className="w-4 h-4 mr-1" />Download PDF
          </Button>
        </div>

        {/* Overall Score */}
        <Card className={`glass-card ${allPass ? 'border-[#00A6A0]/50' : 'border-amber-500/50'}`}>
          <CardContent className="p-6 text-center">
            <div className={`text-5xl font-bold mb-2 ${allPass ? 'text-[#00A6A0]' : 'text-amber-400'}`} data-testid="pillar-score">
              {passCount}/{totalPillars}
            </div>
            <p className="text-lg font-medium text-foreground">{allPass ? 'All Pillars Compliant' : 'Pillars Assessed'}</p>
            <p className="text-sm text-muted-foreground mt-1">{data?.month} Assessment</p>
            {allPass && (
              <div className="mt-3 inline-flex items-center gap-1.5 bg-[#00A6A0]/10 text-[#00A6A0] px-4 py-1.5 rounded-full text-sm font-medium" data-testid="all-pass-badge">
                <CheckCircle2 className="w-4 h-4" />Full Compliance Achieved
              </div>
            )}
          </CardContent>
        </Card>

        {/* Region & Applicable Frameworks */}
        {data?.frameworks && data.frameworks.length > 0 && (
          <Card className="glass-card border-border" data-testid="region-frameworks-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-sm font-medium text-foreground">Applicable Compliance Frameworks</p>
                  <p className="text-[10px] text-muted-foreground">Region: {data.region_label || data.region}</p>
                </div>
                <span className="text-xs bg-[#00A6A0]/10 text-[#00A6A0] px-2.5 py-1 rounded-full font-medium" data-testid="region-badge">{data.region}</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {data.frameworks.map(fw => (
                  <div key={fw.id} className="flex items-center gap-2 p-2 rounded-lg bg-muted/50 border border-border" data-testid={`framework-${fw.id}`}>
                    <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: fw.color }} />
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-foreground truncate">{fw.name}</p>
                      <p className="text-[10px] text-muted-foreground truncate">{fw.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* 12 Pillars Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {(data?.pillars || []).map(p => {
            const Icon = PILLAR_ICONS[p.num] || Shield;
            const isPass = p.status === 'pass';
            return (
              <Card key={p.num} className={`glass-card border-border transition-all hover:shadow-md ${isPass ? '' : 'border-amber-500/30'}`} data-testid={`pillar-${p.num}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isPass ? 'bg-[#00A6A0]/10' : 'bg-amber-500/10'}`}>
                        <Icon className={`w-4 h-4 ${isPass ? 'text-[#00A6A0]' : 'text-amber-400'}`} />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground">{p.name}</p>
                        <p className="text-[10px] text-muted-foreground">{p.desc}</p>
                      </div>
                    </div>
                    <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${isPass ? 'bg-[#A8E6CF]/10 text-[#A8E6CF]' : 'bg-amber-500/10 text-amber-400'}`}>
                      {isPass ? <CheckCircle2 className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
                      {p.status.toUpperCase()}
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-2xl font-bold text-foreground">{p.metric != null ? p.metric.toLocaleString() : '-'}</span>
                    <span className="text-[10px] text-muted-foreground">evidence items</span>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Disclaimer */}
        <Card className="glass-card border-border">
          <CardContent className="p-3">
            <p className="text-[10px] text-muted-foreground">
              This certificate is auto-generated from live system data and is intended for internal board reporting. It does not constitute an external audit or CQC rating. All metrics are traceable to source records in FendanaCura. Download the PDF for a printable version with signature fields.
            </p>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
